import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { 
  getCountryFlag, 
  getSortedCountries, 
  CountryInfo,
  GLOBAL_IDENTIFIER
} from '../utils/countryFlags';

interface CountrySelectorProps {
  selectedCountry: string;
  onChange: (countryCode: string) => void;
  className?: string;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({ 
  selectedCountry = GLOBAL_IDENTIFIER, 
  onChange,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [countries, setCountries] = useState<CountryInfo[]>(getSortedCountries());
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter countries based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setCountries(getSortedCountries());
      return;
    }

    const filtered = getSortedCountries().filter(
      country => country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                country.code.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setCountries(filtered);
  }, [searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Find current country info
  const currentCountry = countries.find(c => c.code === selectedCountry) || 
                        { code: GLOBAL_IDENTIFIER, name: 'Global' };
  
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-2 bg-secondary text-text 
                  rounded-lg border border-border/20 hover:border-accent transition-colors"
      >
        <div className="flex items-center">
          <span className="text-xl mr-3" aria-label={currentCountry.name}>
            {getCountryFlag(currentCountry.code)}
          </span>
          <span>{currentCountry.name}</span>
        </div>
        <span className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full max-h-80 overflow-y-auto bg-cardBg rounded-lg 
                         border border-border/20 shadow-lg">
          {/* Search input */}
          <div className="sticky top-0 bg-secondary p-2 border-b border-border/30">
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-textMuted" />
              </div>
              <input
                type="text"
                placeholder="Search countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-2 pl-10 pr-4 bg-cardBg rounded-md text-text 
                         border border-border/30 focus:outline-none focus:border-accent"
              />
            </div>
          </div>

          {/* Country list */}
          <div className="divide-y divide-border/10">
            {countries.length > 0 ? (
              countries.map((country) => (
                <button
                  key={country.code}
                  onClick={() => {
                    onChange(country.code);
                    setIsOpen(false);
                  }}
                  className={`flex items-center w-full px-4 py-3 text-left hover:bg-secondary/50
                              transition-colors ${selectedCountry === country.code ? 'bg-accent/10' : ''}`}
                >
                  <span className="text-xl mr-3" aria-label={country.name}>
                    {getCountryFlag(country.code)}
                  </span>
                  <span>{country.name}</span>
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-center text-textMuted">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelector;
